import { useState, useEffect, useRef, useCallback } from 'react'
import { Menu, Bell, Search, LogOut, Globe, CalendarCheck, ChevronDown, Check } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useAuth } from '../../hooks/useAuth'
import { useLang } from '../../hooks/useLang'
import toast from 'react-hot-toast'
import { schoolApi } from '../../services/api'

/* ───────────────────────────────────────────────
   My Helper School — DashboardHeader
   Fournit l'année scolaire sélectionnée globalement
   ─────────────────────────────────────────────── */

interface HeaderProps {
  onMenuClick: () => void
}

interface AcademicYear {
  id: number
  label: string
  start_date: string
  end_date: string
  is_current: boolean
  is_selected: boolean
}

// Clé global pour stocker l'année sélectionnée
export const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
export const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
export const SELECTED_YEAR_EVENT = 'lybibal-year-change'

const t = {
  fr: {
    searchPlaceholder: 'Rechercher...',
    logout: 'Deconnexion',
    logoutSuccess: 'Deconnecte',
    academicYear: 'Annee scolaire',
    selectYear: 'Selectionner',
    current: 'Actuel',
    selected: 'Selectionnee',
    loading: 'Chargement...',
    noYears: 'Aucune annee',
  },
  en: {
    searchPlaceholder: 'Search...',
    logout: 'Logout',
    logoutSuccess: 'Logged out',
    academicYear: 'Academic Year',
    selectYear: 'Select',
    current: 'Current',
    selected: 'Selected',
    loading: 'Loading...',
    noYears: 'No years',
  }
}

export default function DashboardHeader({ onMenuClick }: HeaderProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const { user } = useAuthStore()
  const { handleLogout } = useAuth()
  const { lang, toggleLang } = useLang()
  const txt = t[lang]

  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([])
  const [selectedYear, setSelectedYear] = useState<AcademicYear | null>(null)
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false)
  const [yearsLoading, setYearsLoading] = useState(true)
  const [selectingYear, setSelectingYear] = useState(false)
  const yearDropdownRef = useRef<HTMLDivElement>(null)
  const schoolIdRef = useRef<number | null>(null)

  // ─── REF pour syncSelectedYear (évite la recréation à chaque render) ───
  const syncSelectedYearRef = useRef(async (schoolId: number, yearId: number, showToast: boolean = true) => {
    // placeholder — sera écrasé par le useEffect ci-dessous
  })

  // Met à jour la ref quand les dépendances changent (sans déclencher de re-render)
  useEffect(() => {
    syncSelectedYearRef.current = async (schoolId: number, yearId: number, showToast: boolean = true) => {
      try {
        setSelectingYear(true)
        const res: any = await schoolApi.setSelectedAcademicYear(schoolId, yearId)
        const updatedYear = res?.data || res

        setAcademicYears(prev => prev.map(y => ({
          ...y,
          is_selected: y.id === yearId
        })))
        setSelectedYear(prev => prev ? { ...prev, is_selected: prev.id === yearId } : null)

        localStorage.setItem(SELECTED_YEAR_ID_KEY, yearId.toString())
        localStorage.setItem(SELECTED_YEAR_LABEL_KEY, updatedYear.label || academicYears.find(y => y.id === yearId)?.label || '')

        if (showToast) {
          toast.success(
            lang === 'fr'
              ? `Annee "${updatedYear.label || selectedYear?.label}" selectionnee`
              : `Year "${updatedYear.label || selectedYear?.label}" selected`
          )
        }

        // Dispatch event for all components to listen
        window.dispatchEvent(new CustomEvent(SELECTED_YEAR_EVENT, {
          detail: { ...updatedYear, school_id: schoolId, year_id: yearId }
        }))
      } catch (err) {
        console.error('Error selecting year:', err)
        toast.error(lang === 'fr' ? 'Erreur lors de la selection' : 'Error selecting year')
      } finally {
        setSelectingYear(false)
      }
    }
  }, [lang, selectedYear, academicYears])

  // ─── Load academic years ───
  useEffect(() => {
    const loadYears = async () => {
      try {
        const schoolsRes: any = await schoolApi.getSchools()
        const schools = Array.isArray(schoolsRes) ? schoolsRes : schoolsRes?.data || []

        if (schools.length > 0) {
          // ✅ CORRIGÉ : Utiliser le school_id de l'utilisateur connecté
          const userSchoolId = user?.school_id || schools[0]?.id
          schoolIdRef.current = userSchoolId

          const yearsRes: any = await schoolApi.getAcademicYears(userSchoolId)
          const years = Array.isArray(yearsRes) ? yearsRes : yearsRes?.data || []

          if (years.length > 0) {
            setAcademicYears(years)

            // Priorité: 1. is_selected BDD, 2. localStorage, 3. is_current
            const backendSelected = years.find((y: AcademicYear) => y.is_selected === true)
            const storedId = localStorage.getItem(SELECTED_YEAR_ID_KEY)
            const localStored = storedId ? years.find((y: AcademicYear) => y.id.toString() === storedId) : null
            const currentYear = years.find((y: AcademicYear) => y.is_current === true)

            const found = backendSelected || localStored || currentYear || years[0]
            setSelectedYear(found)

            // Stocker dans localStorage pour tous les composants
            localStorage.setItem(SELECTED_YEAR_ID_KEY, found.id.toString())
            localStorage.setItem(SELECTED_YEAR_LABEL_KEY, found.label)

            // Dispatch initial event
            window.dispatchEvent(new CustomEvent(SELECTED_YEAR_EVENT, {
              detail: { ...found, school_id: userSchoolId, year_id: found.id }
            }))

            // Synchroniser avec le backend si nécessaire
            if (!backendSelected && localStored && !localStored.is_selected) {
              await syncSelectedYearRef.current(userSchoolId, localStored.id, false)
            } else if (backendSelected && (!localStored || backendSelected.id.toString() !== storedId)) {
              localStorage.setItem(SELECTED_YEAR_ID_KEY, backendSelected.id.toString())
              localStorage.setItem(SELECTED_YEAR_LABEL_KEY, backendSelected.label)
            }
          }
        } else {
          createFallbackYears()
        }
      } catch (err) {
        console.error('Error loading years:', err)
        createFallbackYears()
      } finally {
        setYearsLoading(false)
      }
    }

    const createFallbackYears = () => {
      const currentYear = new Date().getFullYear()
      const fallback: AcademicYear[] = [
        { id: 1, label: `${currentYear}-${currentYear + 1}`, start_date: `${currentYear}-09-01`, end_date: `${currentYear + 1}-06-30`, is_current: true, is_selected: true },
      ]
      setAcademicYears(fallback)
      setSelectedYear(fallback[0])
      localStorage.setItem(SELECTED_YEAR_ID_KEY, '1')
      localStorage.setItem(SELECTED_YEAR_LABEL_KEY, fallback[0].label)
    }

    loadYears()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.school_id]) // ✅ Dépend de user.school_id pour recharger si changé

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(e.target as Node)) {
        setYearDropdownOpen(false)
      }
    }
    if (yearDropdownOpen) {
      document.addEventListener('mousedown', handler)
      return () => document.removeEventListener('mousedown', handler)
    }
  }, [yearDropdownOpen])

  const handleYearSelect = async (year: AcademicYear) => {
    if (selectedYear?.id === year.id) {
      setYearDropdownOpen(false)
      return
    }

    setSelectedYear(year)
    setYearDropdownOpen(false)

    const schoolId = schoolIdRef.current
    if (!schoolId) return

    await syncSelectedYearRef.current(schoolId, year.id, true)
  }

  const onLogout = () => {
    localStorage.removeItem(SELECTED_YEAR_ID_KEY)
    localStorage.removeItem(SELECTED_YEAR_LABEL_KEY)
    handleLogout()
    toast.success(txt.logoutSuccess)
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <button onClick={onMenuClick} className="lg:hidden p-2 rounded-md text-gray-500 hover:bg-gray-100">
            <Menu className="h-6 w-6" />
          </button>
          <div className="hidden md:flex items-center bg-gray-100 rounded-lg px-3 py-2">
            <Search className="h-4 w-4 text-gray-400 mr-2" />
            <input
              type="text" placeholder={txt.searchPlaceholder}
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-64 placeholder-gray-400"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative hidden sm:block" ref={yearDropdownRef}>
            <button
              onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
              disabled={selectingYear}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                selectedYear?.is_selected
                  ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
              } ${selectingYear ? 'opacity-70 cursor-wait' : ''}`}
            >
              <CalendarCheck className="h-4 w-4" />
              <span className="max-w-[120px] truncate">
                {yearsLoading ? txt.loading : selectedYear ? selectedYear.label : txt.selectYear}
              </span>
              {selectedYear?.is_selected && (
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full uppercase">{txt.selected}</span>
              )}
              {selectedYear?.is_current && !selectedYear?.is_selected && (
                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full uppercase">{txt.current}</span>
              )}
              <ChevronDown className={`h-4 w-4 transition-transform ${yearDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {yearDropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.academicYear}</p>
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {academicYears.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">{txt.noYears}</div>
                  ) : (
                    academicYears.map((year) => (
                      <button
                        key={year.id}
                        onClick={() => handleYearSelect(year)}
                        disabled={selectingYear}
                        className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-50 transition-colors ${
                          selectedYear?.id === year.id ? 'bg-blue-50' : ''
                        } ${selectingYear ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{year.label}</span>
                          {year.is_selected && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">{txt.selected}</span>}
                          {year.is_current && !year.is_selected && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">{txt.current}</span>}
                        </div>
                        {selectedYear?.id === year.id && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <button onClick={toggleLang} className="flex items-center gap-1.5 px-2.5 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
            <Globe className="h-4 w-4" />
            <span className="font-bold">{lang === 'fr' ? 'FR' : 'EN'}</span>
          </button>

          <button className="relative p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors">
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
          </button>

          <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm cursor-default" title={`${user?.first_name || ''} ${user?.last_name || ''}`}>
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>

          <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-lg text-sm font-medium transition-colors" title={txt.logout}>
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  )
}
